import React, { useEffect, useRef } from 'react';
import './BrandWave.css';

// Animated particle wave inspired by the OPTEAM banners and email signatures:
// a perspective field of glowing dots plus a double helix strand, in brand blues.

const CYAN = [101, 208, 235];
const ELECTRIC = [62, 106, 246];
const PERIWINKLE = [143, 178, 238];

const GRID_COLS = 46;
const GRID_ROWS = 16;
const HELIX_DOTS = 70;

const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));

// Pre-render a soft glowing dot so each frame only needs drawImage calls
const makeSprite = (rgb) => {
  const size = 64;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, `rgba(${rgb.join(',')},1)`);
  g.addColorStop(0.35, `rgba(${rgb.join(',')},0.85)`);
  g.addColorStop(1, `rgba(${rgb.join(',')},0)`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return c;
};

const BrandWave = ({ className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.getContext) return undefined;
    const ctx = canvas.getContext('2d');
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const palette = [0, 0.25, 0.5, 0.75, 1].map((t) => makeSprite(mix(CYAN, ELECTRIC, t)));
    const helixSprite = makeSprite(PERIWINKLE);
    const cyanSprite = palette[0];

    let width = 0;
    let height = 0;
    let dpr = 1;
    let frame = null;
    let visible = true;
    let start = null;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (time) => {
      const t = time / 1000;
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';

      const narrow = width < 768;
      const horizon = height * (narrow ? 0.55 : 0.45);
      const focal = Math.min(width, height * 1.6) * 0.55;
      const camHeight = 0.55;
      const centerX = width * (narrow ? 0.5 : 0.62);

      // Perspective dot field, drawn far to near
      for (let j = GRID_ROWS - 1; j >= 0; j--) {
        const v = j / (GRID_ROWS - 1);
        const z = 0.95 + v * 4.4;
        const f = focal / z;
        for (let i = 0; i < GRID_COLS; i++) {
          const u = i / (GRID_COLS - 1);
          const x = (u - 0.5) * 5.2;
          const y = 0.32 * Math.sin(u * 6.5 + t * 0.55 + v * 2.2) * (0.65 + 0.35 * Math.sin(v * 3.1 - t * 0.35))
            + 0.1 * Math.sin(u * 15 - t * 0.8 + v);
          const sx = centerX + x * f;
          const sy = horizon + (camHeight - y) * f * 0.72;
          if (sx < -40 || sx > width + 40 || sy < -40 || sy > height + 40) continue;
          const crest = Math.max(0, Math.min(1, (y + 0.42) / 0.84));
          const r = Math.min(9, Math.max(0.8, f * 0.02)) * (0.7 + crest * 0.6);
          const near = 1 - v;
          ctx.globalAlpha = (0.12 + 0.5 * near) * (0.45 + 0.55 * crest);
          // Nearest rows get a bigger, softer "out of focus" dot like the banner bokeh
          const blur = near > 0.8 ? 2.4 : 1.6;
          const sprite = palette[Math.min(4, Math.floor((1 - crest) * 5))];
          ctx.drawImage(sprite, sx - r * blur, sy - r * blur, r * blur * 2, r * blur * 2);
        }
      }

      // Double helix strand crossing the scene
      const amp = height * (narrow ? 0.09 : 0.13);
      const baseY = height * (narrow ? 0.3 : 0.38);
      for (let k = 0; k < HELIX_DOTS; k++) {
        const p = k / (HELIX_DOTS - 1);
        const sx = width * (narrow ? -0.05 : 0.28) + p * width * (narrow ? 1.1 : 0.8);
        for (let s = 0; s < 2; s++) {
          const phase = p * Math.PI * 4.2 - t * 0.9 + s * Math.PI;
          const depth = (Math.cos(phase) + 1) / 2;
          const sy = baseY + Math.sin(phase) * amp + Math.sin(p * 3 + t * 0.4) * amp * 0.5;
          const r = 1.4 + depth * 3.6;
          ctx.globalAlpha = 0.18 + depth * 0.7;
          const sprite = s === 0 ? cyanSprite : helixSprite;
          ctx.drawImage(sprite, sx - r * 2, sy - r * 2, r * 4, r * 4);
        }
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };

    const loop = (now) => {
      if (start === null) start = now;
      draw(now - start + 8000);
      frame = visible ? window.requestAnimationFrame(loop) : null;
    };

    const play = () => {
      if (!reduceMotion && visible && frame === null && !document.hidden) {
        frame = window.requestAnimationFrame(loop);
      }
    };

    const pause = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
    };

    resize();
    draw(8000);
    play();

    const onResize = () => {
      resize();
      if (reduceMotion || frame === null) draw(8000);
    };
    const onVisibility = () => (document.hidden ? pause() : play());

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        if (visible) play(); else pause();
      });
      observer.observe(canvas);
    }

    return () => {
      pause();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      if (observer) observer.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className={`brand-wave ${className}`} aria-hidden="true" />;
};

export default BrandWave;
